<div class="role17-form-container">
  <p-toast></p-toast>
  <p-confirmDialog></p-confirmDialog>

  <app-reload-banner [visible]="reloadPromptDismissed()" (reload)="onReload()" />

  <app-sheet-loader [visible]="isLoading" />

  <!-- Header Card -->
  <p-card class="form-header-card" *ngIf="schema">
    <div class="header-content">
      <div class="header-title">
        <h2>{{ formTitle }}</h2>
        <p class="header-description">{{ schema.description }}</p>
      </div>
      <div class="header-actions">
        <p-button
          label="🎯 Auto-fill Demo"
          (onClick)="autoFillDemo()"
          severity="secondary"
          [outlined]="true"
          size="small"
        ></p-button>
      </div>
    </div>
  </p-card>

  <!-- Consolidated Status Notice -->
  <div *ngIf="isConsolidated" class="consolidated-notice">
    <i class="pi pi-info-circle"></i>
    <span>Consolidated financial statements - showing Group and Company columns</span>
  </div>

  <!-- Main Form Card -->
  <p-card class="form-card" *ngIf="form && schema">
    <form [formGroup]="form">
      <!-- Statement of Cash Flows Table -->
      <div class="financial-statement-table-wrapper">
        <table class="financial-statement-table"
          appFieldLinkTarget
          fieldLinkSheet="statement-of-cash-flows-direct"
          [fieldLinkControls]="schema.controls"
        >
          <thead>
            <tr>
              <th class="description-column">STATEMENT OF CASH FLOWS</th>

              <!-- Scenario 1: Consolidated + Comparative (4 columns) -->
              <ng-container *ngIf="isConsolidated && hasPreviousYear">
                <th class="amount-column">Group<br/><span class="period-header">{{ periodHeaderCurrent }}</span><br/>{{ currencyUnit }}</th>
                <th class="amount-column">Group<br/><span class="period-header">{{ periodHeaderPrevious }}</span><br/>{{ currencyUnit }}</th>
                <th class="amount-column">Company<br/><span class="period-header">{{ periodHeaderCurrent }}</span><br/>{{ currencyUnit }}</th>
                <th class="amount-column">Company<br/><span class="period-header">{{ periodHeaderPrevious }}</span><br/>{{ currencyUnit }}</th>
              </ng-container>

              <!-- Scenario 2: Consolidated only (2 columns) -->
              <ng-container *ngIf="isConsolidated && !hasPreviousYear">
                <th class="amount-column">Group<br/><span class="period-header">{{ periodHeaderCurrent }}</span><br/>{{ currencyUnit }}</th>
                <th class="amount-column">Company<br/><span class="period-header">{{ periodHeaderCurrent }}</span><br/>{{ currencyUnit }}</th>
              </ng-container>

              <!-- Scenario 3: Comparative only (2 columns) -->
              <ng-container *ngIf="!isConsolidated && hasPreviousYear">
                <th class="amount-column"><span class="period-header">{{ periodHeaderCurrent }}</span><br/>{{ currencyUnit }}</th>
                <th class="amount-column"><span class="period-header">{{ periodHeaderPrevious }}</span><br/>{{ currencyUnit }}</th>
              </ng-container>

              <!-- Scenario 4: Neither (1 column) -->
              <ng-container *ngIf="!isConsolidated && !hasPreviousYear">
                <th class="amount-column"><span class="period-header">{{ periodHeaderCurrent }}</span><br/>{{ currencyUnit }}</th>
              </ng-container>
            </tr>
          </thead>
          <tbody>
            <ng-container *ngFor="let control of schema?.controls || []">
              <!-- Section Headers -->
              <tr *ngIf="control.type === 'SectionHeader'" [ngClass]="'header-level-' + control.ui.level">
                <td [attr.colspan]="getColspan()" class="section-header">
                  {{ control.label }}
                </td>
              </tr>

              <!-- Monetary Fields with Dynamic Columns -->
              <tr *ngIf="control.type === 'MonetaryItemType'" [attr.data-qname]="control.qname" [ngClass]="{'calculated-row': control.ui.calculated, 'total-row': control.label.toLowerCase().includes('net cash')}">
                <td class="description-column">
                  <span>{{ control.label }}</span><app-required-marker [when]="isFieldRequired(control)" />
                  <i *ngIf="control.ui.note && fieldNoteTooltipsEnabled()"
                     class="pi pi-info-circle info-icon"
                     [pTooltip]="control.ui.note"
                     tooltipPosition="right"></i>
                </td>

                <!-- Scenario 1: Consolidated + Comparative (4 columns) -->
                <ng-container *ngIf="isConsolidated && hasPreviousYear">
                  <td class="amount-column">
                    <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                      [formControlName]="control.qname + 'GroupCurrent'"
                      mode="decimal"
                      [minFractionDigits]="numericPolicy(control).decimals"
                      [maxFractionDigits]="numericPolicy(control).decimals"
                      [useGrouping]="numericPolicy(control).grouping"
                      prefix="RM "
                      [disabled]="!!control.ui.calculated"
                      [style]="{'width': '100%'}"
                    ></p-inputnumber>
                  </td>
                  <td class="amount-column">
                    <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                      [formControlName]="control.qname + 'GroupPrevious'"
                      mode="decimal"
                      [minFractionDigits]="numericPolicy(control).decimals"
                      [maxFractionDigits]="numericPolicy(control).decimals"
                      [useGrouping]="numericPolicy(control).grouping"
                      prefix="RM "
                      [disabled]="!!control.ui.calculated"
                      [style]="{'width': '100%'}"
                    ></p-inputnumber>
                  </td>
                  <td class="amount-column">
                    <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                      [formControlName]="control.qname + 'CompanyCurrent'"
                      mode="decimal"
                      [minFractionDigits]="numericPolicy(control).decimals"
                      [maxFractionDigits]="numericPolicy(control).decimals"
                      [useGrouping]="numericPolicy(control).grouping"
                      prefix="RM "
                      [disabled]="!!control.ui.calculated"
                      [style]="{'width': '100%'}"
                    ></p-inputnumber>
                  </td>
                  <td class="amount-column">
                    <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                      [formControlName]="control.qname + 'CompanyPrevious'"
                      mode="decimal"
                      [minFractionDigits]="numericPolicy(control).decimals"
                      [maxFractionDigits]="numericPolicy(control).decimals"
                      [useGrouping]="numericPolicy(control).grouping"
                      prefix="RM "
                      [disabled]="!!control.ui.calculated"
                      [style]="{'width': '100%'}"
                    ></p-inputnumber>
                  </td>
                </ng-container>

                <!-- Scenario 2: Consolidated only (2 columns) -->
                <ng-container *ngIf="isConsolidated && !hasPreviousYear">
                  <td class="amount-column">
                    <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                      [formControlName]="control.qname + 'GroupCurrent'"
                      mode="decimal"
                      [minFractionDigits]="numericPolicy(control).decimals"
                      [maxFractionDigits]="numericPolicy(control).decimals"
                      [useGrouping]="numericPolicy(control).grouping"
                      prefix="RM "
                      [disabled]="!!control.ui.calculated"
                      [style]="{'width': '100%'}"
                    ></p-inputnumber>
                  </td>
                  <td class="amount-column">
                    <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                      [formControlName]="control.qname + 'CompanyCurrent'"
                      mode="decimal"
                      [minFractionDigits]="numericPolicy(control).decimals"
                      [maxFractionDigits]="numericPolicy(control).decimals"
                      [useGrouping]="numericPolicy(control).grouping"
                      prefix="RM "
                      [disabled]="!!control.ui.calculated"
                      [style]="{'width': '100%'}"
                    ></p-inputnumber>
                  </td>
                </ng-container>

                <!-- Scenario 3: Comparative only (2 columns) -->
                <ng-container *ngIf="!isConsolidated && hasPreviousYear">
                  <td class="amount-column">
                    <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                      [formControlName]="control.qname + 'Current'"
                      mode="decimal"
                      [minFractionDigits]="numericPolicy(control).decimals"
                      [maxFractionDigits]="numericPolicy(control).decimals"
                      [useGrouping]="numericPolicy(control).grouping"
                      prefix="RM "
                      [disabled]="!!control.ui.calculated"
                      [style]="{'width': '100%'}"
                    ></p-inputnumber>
                  </td>
                  <td class="amount-column">
                    <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                      [formControlName]="control.qname + 'Previous'"
                      mode="decimal"
                      [minFractionDigits]="numericPolicy(control).decimals"
                      [maxFractionDigits]="numericPolicy(control).decimals"
                      [useGrouping]="numericPolicy(control).grouping"
                      prefix="RM "
                      [disabled]="!!control.ui.calculated"
                      [style]="{'width': '100%'}"
                    ></p-inputnumber>
                  </td>
                </ng-container>

                <!-- Scenario 4: Neither (1 column) -->
                <ng-container *ngIf="!isConsolidated && !hasPreviousYear">
                  <td class="amount-column">
                    <p-inputnumber appSchemaNumeric [schemaControl]="control" appNegativeHighlight
                      [formControlName]="control.qname + 'Current'"
                      mode="decimal"
                      [minFractionDigits]="numericPolicy(control).decimals"
                      [maxFractionDigits]="numericPolicy(control).decimals"
                      [useGrouping]="numericPolicy(control).grouping"
                      prefix="RM "
                      [disabled]="!!control.ui.calculated"
                      [style]="{'width': '100%'}"
                    ></p-inputnumber>
                  </td>
                </ng-container>
              </tr>
            </ng-container>
          </tbody>
        </table>
      </div>

      <!-- Form Actions -->
      <div class="form-actions">
        <p-button
          label="Clear Form"
          icon="pi pi-times"
          (onClick)="clearForm()"
          severity="secondary"
          [outlined]="true"
        ></p-button>

        <div class="flex flex-col items-end gap-1">
          <p-button
            label="Submit"
            icon="pi pi-check"
            (onClick)="submitForm()"
            severity="success"
            [disabled]="reloadPromptDismissed()"
          ></p-button>
          <small
            *ngIf="reloadPromptDismissed()"
            class="text-xs text-amber-700"
          >
            Please refresh the page to obtain the latest version of this sheet.
          </small>
        </div>
      </div>
    </form>
  </p-card>

  <app-reload-prompt
    [visible]="showReloadPrompt()"
    mode="return-or-stay"
    title="Filing regenerated"
    message="Core settings were just submitted. The data on this page is being regenerated — return to files list, or stay on this view (it may be out of date)."
    (reload)="onReload()"
    (dismiss)="onDismiss()"
    (returnToFiles)="onReturnToFiles()"
  ></app-reload-prompt>
</div>
